import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Search, MapPin, Briefcase, DollarSign, Filter, ExternalLink, Bookmark, BookmarkCheck } from "lucide-react";
import JobDetailsModal from "@/components/JobDetailsModal";

interface Opportunity {
  id: string;
  title: string;
  company_name: string;
  location: string;
  employment_type: string;
  description: string;
  salary_min: number;
  salary_max: number;
  disclosure_level_required: number;
  requirements: string | null;
  created_at: string;
}

export default function FindJobs() {
  const { toast } = useToast();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState("all");
  const [selectedJob, setSelectedJob] = useState<Opportunity | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [appliedJobIds, setAppliedJobIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOpportunities();
    fetchSavedJobs();
    fetchApplications();
  }, []);

  useEffect(() => {
    filterOpportunities();
  }, [searchTerm, locationFilter, employmentTypeFilter, opportunities]);

  const fetchOpportunities = async () => {
    try {
      // Security: RLS policies ensure only active, candidate-friendly opportunities are visible
      const { data, error } = await supabase
        .from("opportunities")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setOpportunities(data || []);
      setFilteredOpportunities(data || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("saved_jobs")
        .select("opportunity_id")
        .eq("candidate_id", user.id);

      if (error) throw error;

      setSavedJobIds(new Set(data?.map(j => j.opportunity_id) || []));
    } catch (error) {
      console.error("Error fetching saved jobs:", error);
    }
  };

  const fetchApplications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("job_applications")
        .select("job_posting_id")
        .eq("candidate_id", user.id);

      if (error) throw error;

      setAppliedJobIds(new Set(data?.map(a => a.job_posting_id) || []));
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const filterOpportunities = () => {
    let filtered = [...opportunities];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (opp) =>
          opp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          opp.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          opp.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Location filter
    if (locationFilter !== "all") {
      filtered = filtered.filter((opp) => opp.location === locationFilter);
    }

    // Employment type filter
    if (employmentTypeFilter !== "all") {
      filtered = filtered.filter((opp) => opp.employment_type === employmentTypeFilter);
    }

    setFilteredOpportunities(filtered);
  };

  const formatSalary = (min: number, max: number) => {
    if (min && max) {
      return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    }
    return "Competitive";
  };

  const getDisclosureBadge = (level: number) => {
    const labels = ["No Check", "Basic DBS", "Standard DBS", "Enhanced DBS"];
    const variants: ("secondary" | "outline" | "default")[] = ["secondary", "outline", "outline", "default"];
    return { label: labels[level] || "Unknown", variant: variants[level] || "secondary" };
  };

  const handleViewJob = async (opportunity: Opportunity) => {
    setSelectedJob(opportunity);
    setModalOpen(true);

    // Track job view
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("job_views").insert({
        candidate_id: user.id,
        job_posting_id: opportunity.id,
      });
    } catch (error) {
      // Silently fail - view tracking is not critical
      console.error("Error tracking view:", error);
    }
  };

  const handleSaveToggle = async (opportunityId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const isSaved = savedJobIds.has(opportunityId);

      if (isSaved) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("candidate_id", user.id)
          .eq("opportunity_id", opportunityId);

        if (error) throw error;

        setSavedJobIds(prev => {
          const next = new Set(prev);
          next.delete(opportunityId);
          return next;
        });

        toast({
          title: "Removed from Saved",
          description: "Job removed from your saved list.",
        });
      } else {
        const { error } = await supabase.from("saved_jobs").insert({
          candidate_id: user.id,
          opportunity_id: opportunityId,
        });

        if (error) throw error;

        setSavedJobIds(prev => new Set(prev).add(opportunityId));

        toast({
          title: "Job Saved",
          description: "Job added to your saved list.",
        });
      }
    } catch (error: any) {
      console.error("Save error:", error);
      toast({
        title: "Error Saving Job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const uniqueLocations = Array.from(new Set(opportunities.map((o) => o.location)));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Opportunities
          </CardTitle>
          <CardDescription>
            Find employers who believe in second chances
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by job title, company, or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Location
              </label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {uniqueLocations.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Employment Type
              </label>
              <Select value={employmentTypeFilter} onValueChange={setEmploymentTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="full-time">Full-time</SelectItem>
                  <SelectItem value="part-time">Part-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{filteredOpportunities.length} opportunities found</span>
            {(searchTerm || locationFilter !== "all" || employmentTypeFilter !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setLocationFilter("all");
                  setEmploymentTypeFilter("all");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        {filteredOpportunities.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Filter className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No opportunities match your search criteria.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchTerm("");
                  setLocationFilter("all");
                  setEmploymentTypeFilter("all");
                }}
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredOpportunities.map((opp) => {
            const disclosure = getDisclosureBadge(opp.disclosure_level_required);
            const isSaved = savedJobIds.has(opp.id);
            const hasApplied = appliedJobIds.has(opp.id);
            return (
              <Card key={opp.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewJob(opp)}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1 flex-1">
                      <CardTitle className="text-xl">{opp.title}</CardTitle>
                      <CardDescription className="text-base font-medium text-foreground">
                        {opp.company_name}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="capitalize">
                        {opp.employment_type.replace("-", " ")}
                      </Badge>
                      <Button
                        variant={isSaved ? "default" : "outline"}
                        size="icon"
                        onClick={(e) => handleSaveToggle(opp.id, e)}
                      >
                        {isSaved ? (
                          <BookmarkCheck className="h-4 w-4" />
                        ) : (
                          <Bookmark className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasApplied && (
                    <Badge variant="default" className="bg-green-600">
                      Applied
                    </Badge>
                  )}

                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {opp.description}
                  </p>

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{opp.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>{formatSalary(opp.salary_min, opp.salary_max)}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <Badge variant={disclosure.variant as any}>
                      {disclosure.label}
                    </Badge>
                    <Button onClick={(e) => { e.stopPropagation(); handleViewJob(opp); }}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <JobDetailsModal
        opportunity={selectedJob}
        open={modalOpen}
        onOpenChange={setModalOpen}
        isSaved={selectedJob ? savedJobIds.has(selectedJob.id) : false}
        hasApplied={selectedJob ? appliedJobIds.has(selectedJob.id) : false}
        onSaveToggle={() => {
          fetchSavedJobs();
        }}
        onApply={() => {
          fetchApplications();
        }}
      />
    </div>
  );
}
